import { KeyRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { BusinessDetailsView } from '@quds/shared';
import { EmptyState, LoadingState, Modal } from '@/components/ui';
import { getBusinessDetails } from '@/data';
import { DeviceLicenseManager } from '@/features/pharmacies/licenses/DeviceLicenseManager';
import type { ActionModalProps } from '../types';

/** Thin modal host — Device License UI is shared with the Business workspace License tab. */
export function ManageLicenseModal({ pharmacy, onClose }: ActionModalProps) {
  const [details, setDetails] = useState<BusinessDetailsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setDetails(await getBusinessDetails(pharmacy.id));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load license details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacy.id]);

  return (
    <Modal
      open
      onClose={onClose}
      title="Manage License"
      description={pharmacy.name}
      icon={<KeyRound size={18} aria-hidden="true" />}
      size="xl"
    >
      {loading && !details && <LoadingState className="py-10" label="Loading Device Licenses…" />}
      {loadError && !details && <EmptyState title="Could not load licenses" description={loadError} />}
      {details && (
        <DeviceLicenseManager
          businessId={pharmacy.id}
          businessName={pharmacy.name}
          categoryKey={details.overview.category}
          details={details}
          onRefresh={reload}
        />
      )}
    </Modal>
  );
}
